import React, { useEffect } from "react";
import { Box, Typography, TextField, styled, Button } from "@mui/material";
import { PinIcon, LocationIcon } from "../../assets/icons";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Hero Section Styles
const HeroSection = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  height: "779px",
  backgroundImage: "url(/images/section1.svg)",
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "&::before": {
    content: '""',
    position: "absolute",
    top: "-109px",
    left: "-70px",
    width: "2125px",
    height: "819px",
    zIndex: 0,
  },
  [theme.breakpoints.down("lg")]: {
    height: "600px",
  },
  [theme.breakpoints.down("md")]: {
    height: "600px",
    paddingTop: "0px", // Add sufficient padding for 70px static header + clearance
  },
}));

const HeroContent = styled(Box)(({ theme }) => ({
  position: "relative",
  zIndex: 2,
  display: "flex",
  width: "1440px",
  maxWidth: "100%",
  padding: "0 130px",
  alignItems: "center",
  gap: "80px",
  [theme.breakpoints.down("lg")]: {
    flexDirection: "column",
    padding: "0 24px", // Unified mobile padding
    gap: "32px",
  },
  [theme.breakpoints.down("md")]: {
    padding: "24px 20px 0", // Consistent mobile padding
    gap: "24px",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "16px 16px 0", // Small mobile padding
    gap: "40px",
  },
}));

const HeroTextSection = styled(Box)(({ theme }) => ({
  width: "548px",
  display: "flex",
  flexDirection: "column",
  gap: "24px", // Consistent gap
  [theme.breakpoints.down("lg")]: {
    width: "100%",
    textAlign: "center",
  },
  [theme.breakpoints.down("md")]: {
    gap: "20px", // Mobile gap
  },
  [theme.breakpoints.down("sm")]: {
    gap: "16px", // Small mobile gap
  },
}));

const HeroTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "Inter",
  fontWeight: 700,
  fontSize: "32px", // Unified large title size
  lineHeight: "1.3",
  color: "#14582D",
  [theme.breakpoints.down("md")]: {
    fontSize: "24px", // Mobile large title
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "24px", // Small mobile large title
  },
}));

const HeroSubtitle = styled(Typography)(({ theme }) => ({
  fontFamily: "Inter",
  fontWeight: 400,
  fontSize: "18px", // Unified subtitle size
  lineHeight: "1.5",
  color: "#14582D",
  [theme.breakpoints.down("md")]: {
    fontSize: "16px", // Mobile subtitle
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "14px", // Small mobile subtitle
  },
}));

const AddressInputContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  width: "387px",
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
}));

const AddressInputField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#FFFFFF",
    borderRadius: "12px",
    height: "56px",
    display: "flex",
    alignItems: "center",
    padding: "16px",
    "& fieldset": {
      borderColor: "#CFD1D5",
    },
    "&:hover fieldset": {
      borderColor: "#29BC62",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#29BC62",
    },
    "& .MuiInputBase-input::placeholder": {
      color: theme.palette.text.fontDarkest,
      opacity: 1, // Ensure full opacity across all browsers
    },
    "& input::placeholder": {
      color: theme.palette.text.fontDarkest,
      opacity: 1,
    },
  },
}));

const PhoneMockup = styled(Box)(({ theme }) => ({
  width: "703px",
  height: "689px",
  backgroundImage: "url(/images/hero-phone-mockup-7e281e.png)",
  backgroundSize: "cover",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "center",
  animation: "float 6s ease-in-out infinite",
  "@keyframes float": {
    "0%": { transform: "translateY(0px)" },
    "50%": { transform: "translateY(-20px)" },
    "100%": { transform: "translateY(0px)" },
  },
  [theme.breakpoints.down("lg")]: {
    width: "400px",
    height: "392px",
  },
  [theme.breakpoints.down("sm")]: {
    width: "300px",
    height: "294px",
  },
}));

const HeroButton = styled(Button)(({ theme }) => ({
  background: "#F5933D",
  color: "#FFFFFF",
  borderRadius: "12px",
  padding: "16px 40px",
  fontSize: "16px",
  fontWeight: 600,
  fontFamily: "Inter",
  textTransform: "none",
  maxWidth: "200px",
  "&:hover": {
    background: "#E8843A",
    transform: "translateY(-2px)",
    boxShadow: "0 4px 12px rgba(245, 147, 61, 0.3)",
  },
  transition: "all 0.3s ease",
}));

const HeroSectionComponent: React.FC = () => {
  const { t, i18n } = useTranslation();

  return (
    <HeroSection>
      <HeroContent>
        <HeroTextSection>
          <HeroTitle>
            {t('common.heroSection.title')}
            <br />
            {t('common.heroSection.titleLine2')}
          </HeroTitle>
          <HeroSubtitle>{t('common.heroSection.subtitle')}</HeroSubtitle>
          {/* <AddressInputContainer>
            <AddressInputField
              fullWidth
              placeholder="Enter your address"
              InputProps={{
                startAdornment: (
                  <Typography
                    sx={{
                      color: "text.fontDark",
                      mr: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <PinIcon />
                  </Typography>
                ),
                endAdornment: (
                  <Typography
                    sx={{ color: "text.fontDark", display: "flex", alignItems: "center", justifyContent: "center" }}
                  >
                    <LocationIcon />
                  </Typography>
                ),
              }}
            />
          </AddressInputContainer> */}
          <Link style={{ textDecoration: "none", color: "#FFFFFF" }} to={"/partners"}>
            <HeroButton>{t('common.heroSection.exploreButton')}</HeroButton>
          </Link>
        </HeroTextSection>
        <PhoneMockup />
      </HeroContent>
    </HeroSection>
  );
};

export default HeroSectionComponent;
